import React, { useEffect, useState } from 'react';
import { X, Loader2, Lock, Globe } from 'lucide-react';
import { createCustomAgent, updateCustomAgent, type CustomAgent, type CustomAgentInput } from '../../services/customAgentService';
import { usePlan } from '../../contexts/PlanContext';
import AgentKnowledgeBase from './AgentKnowledgeBase';
import { flowOverlay, flowPanel, modalHeader, modalTitle, modalFooter, modalLabel, modalField } from '../ui/modalSpacing';
import { TYPE } from '../../constants/typeScale';

/**
 * Create / edit a persistent custom agent ("My Agents"). On create we save the basics;
 * once the agent exists, the knowledge section (shared AgentKnowledgeBase) ingests supporting
 * material (text / URL / documents) into its knowledge base — retrieved via RAG in chat.
 */
// The ABSTRACTED models an agent can use (never raw model ids — the catalog maps these to
// real models server-side). The agent runs the lightest one for simple turns and escalates
// up to the chosen ceiling when a task needs it.
const TIER_INFO: Array<{ id: CustomAgent['model_tier']; label: string; desc: string }> = [
  { id: 'SMART', label: 'Smart', desc: 'everyday reasoning & writing' },
  { id: 'EXPERT', label: 'Expert', desc: 'deeper, rigorous analysis' },
  { id: 'RESEARCH', label: 'Research', desc: 'live web research & grounding' },
];
const TOOLS = [
  { id: 'summary', label: 'Summary' },
  { id: 'diagram', label: 'Diagram' },
  { id: 'deck', label: 'Deck' },
];

interface Props {
  open: boolean;
  agent: CustomAgent | null; // null = create mode
  onClose: () => void;
  onSaved: (agent: CustomAgent) => void;
}

const AgentEditorModal: React.FC<Props> = ({ open, agent, onClose, onSaved }) => {
  const { planTier } = usePlan();
  const isSubscriber = planTier !== 'FREE';
  const [form, setForm] = useState<CustomAgentInput>({});
  const [saved, setSaved] = useState<CustomAgent | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    const a = agent;
    setSaved(a);
    setForm(a
      ? { name: a.name, persona: a.persona, description: a.description, focus: a.focus, system_prompt: a.system_prompt, model_tier: a.model_tier, temperature: a.temperature, tools: a.tools, knowledge_backend: a.knowledge_backend, avatar_url: a.avatar_url, visibility: a.visibility }
      : { model_tier: 'SMART', temperature: 0.6, tools: [], knowledge_backend: 'simple', visibility: 'public' });
  }, [open, agent]);

  if (!open) return null;

  const set = (patch: CustomAgentInput) => setForm((f) => ({ ...f, ...patch }));
  const toggleTool = (id: string) => set({ tools: (form.tools || []).includes(id) ? (form.tools || []).filter((t) => t !== id) : [...(form.tools || []), id] });

  const save = async () => {
    if (!form.name?.trim()) { setError('Give your agent a name.'); return; }
    setBusy(true); setError('');
    try {
      const result = saved ? await updateCustomAgent(saved.id, form) : await createCustomAgent(form);
      setSaved(result);
      onSaved(result);
      if (!agent) return; // created: keep open so they can add knowledge, now in edit mode
    } catch (e: any) { setError(e?.message || 'Could not save the agent'); } finally { setBusy(false); }
  };

  return (
    <div className={`${flowOverlay} z-50`} onClick={onClose}>
      <div className={`${flowPanel} sm:w-full sm:max-w-lg sm:h-auto sm:max-h-[90dvh]`} onClick={(e) => e.stopPropagation()}>
        <div className={modalHeader}>
          <h2 className={modalTitle}>{saved ? 'Edit agent' : 'Create an agent'}</h2>
          <button onClick={onClose} aria-label="Close" title="Close" className="text-secondary-400 hover:text-secondary-700 dark:hover:text-white"><X className="h-4 w-4" /></button>
        </div>

        <div className="overflow-y-auto px-6 py-6 space-y-5">
          {error && <p className={`${TYPE.body} text-secondary-500 dark:text-secondary-400`}>{error}</p>}

          <div className="space-y-3">
            <input value={form.name || ''} onChange={(e) => set({ name: e.target.value })} placeholder="Name (e.g. Quinn)" className={modalField} />
            <input value={form.persona || ''} onChange={(e) => set({ persona: e.target.value })} placeholder="Role (e.g. Quant Analyst)" className={modalField} />
            <input value={form.focus || ''} onChange={(e) => set({ focus: e.target.value })} placeholder="Focus — what should it be great at? (e.g. financial modelling for startups)" className={modalField} />
            <textarea value={form.system_prompt || ''} onChange={(e) => set({ system_prompt: e.target.value })} rows={4} placeholder="Instructions (optional) — how should this agent think and respond?" className={`${modalField} resize-none`} />
          </div>

          <section>
            <p className={modalLabel}>Models it can use</p>
            <div className="flex gap-2">
              <select value={form.model_tier} onChange={(e) => set({ model_tier: e.target.value as CustomAgent['model_tier'] })} className={modalField}>
                {TIER_INFO.map((t) => <option key={t.id} value={t.id}>{t.label} — {t.desc}</option>)}
              </select>
              <input value={form.avatar_url || ''} onChange={(e) => set({ avatar_url: e.target.value })} placeholder="Avatar image URL (optional)" className={modalField} />
            </div>
            <p className={`mt-2 ${TYPE.meta} text-secondary-400`}>Uses the lightest model for simple turns and escalates up to this when a task needs it.</p>
          </section>

          <section>
            <p className={modalLabel}>Tools it can use</p>
            <div className="flex gap-2">
              {TOOLS.map((t) => (
                <button key={t.id} type="button" onClick={() => toggleTool(t.id)}
                  className={`px-3 py-1.5 rounded-full ${TYPE.control} border ${(form.tools || []).includes(t.id) ? 'border-secondary-900 dark:border-white bg-secondary-900 dark:bg-white text-white dark:text-secondary-900' : 'border-secondary-300 dark:border-secondary-600 text-secondary-600 dark:text-secondary-300'}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </section>

          {/* Visibility — private is a subscriber perk (hides instructions from others in shared chats) */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <p className={`${TYPE.label} text-secondary-400`}>Visibility</p>
              {!isSubscriber && <span className={`${TYPE.micro} text-secondary-400`}>Subscribe to make private</span>}
            </div>
            <button type="button" disabled={!isSubscriber}
              onClick={() => set({ visibility: form.visibility === 'private' ? 'public' : 'private' })}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${TYPE.control} border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${form.visibility === 'private' ? 'border-secondary-900 dark:border-white bg-secondary-900 dark:bg-white text-white dark:text-secondary-900' : 'border-secondary-300 dark:border-secondary-600 text-secondary-600 dark:text-secondary-300'}`}>
              {form.visibility === 'private' ? <Lock className="w-3.5 h-3.5" /> : <Globe className="w-3.5 h-3.5" />}
              {form.visibility === 'private' ? 'Private' : 'Public'}
            </button>
            <p className={`mt-2 ${TYPE.meta} text-secondary-400`}>Private agents keep their instructions hidden from other people you share a chat with.</p>
          </section>

          {/* Knowledge base — shared component; available once the agent exists */}
          {saved
            ? <AgentKnowledgeBase agentKey={saved.id} onError={setError} />
            : <p className={`${TYPE.control} text-secondary-400`}>Save the agent first, then add links and documents to its knowledge base here.</p>}
        </div>

        <div className={modalFooter}>
          <button onClick={onClose} className={`px-3 py-2 ${TYPE.body} text-secondary-500 hover:text-secondary-800 dark:hover:text-secondary-200`}>{saved ? 'Done' : 'Cancel'}</button>
          <button onClick={save} disabled={busy} className={`inline-flex items-center gap-2 rounded-lg bg-secondary-900 dark:bg-white px-4 py-2 ${TYPE.sectionTitle} text-white dark:text-secondary-900 disabled:opacity-40`}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}{saved ? 'Save changes' : 'Create agent'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgentEditorModal;
